import { useState } from 'react'
import { Menu, X } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import logo from '@/assets/logo-fotolivro.png'
import SmoothScrollLink from './SmoothScrollLink'
import { useActiveSection } from '@/hooks/useActiveSection'
import { cn } from '@/lib/utils'

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()
  const activeSection = useActiveSection([
    'o-que-e',
    'beneficios',
    'como-funciona',
    'faq',
  ])

  const navItems = [
    { label: 'O que é?', href: '/parcerias#o-que-e', sectionId: 'o-que-e' },
    {
      label: 'Benefícios',
      href: '/parcerias#beneficios',
      sectionId: 'beneficios',
    },
    {
      label: 'Como funciona',
      href: '/parcerias#como-funciona',
      sectionId: 'como-funciona',
    },
    {
      label: 'Sobre a FotoLivro Store',
      href: '/parcerias/sobre',
      sectionId: null,
    },
    { label: 'FAQ', href: '/parcerias#faq', sectionId: 'faq' },
  ]

  const isActive = (item: (typeof navItems)[0]) => {
    // For section links on homepage
    if (item.sectionId && location.pathname === '/') {
      return activeSection === item.sectionId
    }
    // For page links like /sobre
    if (!item.sectionId && location.pathname === item.href) {
      return true
    }
    return false
  }

  return (
    <div className="sticky top-0 z-50">
      {/* Main Header */}
      <header className="w-full py-4 px-6 md:px-12 lg:px-20 bg-background border-b border-border">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo - Home button */}
          <SmoothScrollLink href="/#" className="flex items-center group">
            <img
              src={logo}
              alt="FotoLivro Store"
              className="h-10 md:h-12 w-auto"
            />
          </SmoothScrollLink>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map(item => (
              <SmoothScrollLink
                key={item.label}
                href={item.href}
                className={cn(
                  'text-sm font-medium tracking-wide font-sans transition-all duration-300 relative',
                  isActive(item)
                    ? 'text-primary after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-0.5 after:bg-primary after:rounded-full'
                    : 'text-muted-foreground hover:text-primary',
                )}
              >
                {item.label}
              </SmoothScrollLink>
            ))}
            <SmoothScrollLink
              href="/parcerias/cadastro"
              className="text-sm font-semibold text-primary border-2 border-primary hover:bg-primary hover:text-primary-foreground px-5 py-2 rounded-full transition-colors font-sans"
            >
              Cadastre-se
            </SmoothScrollLink>
            <a
              href="https://fotoinfluencer.com.br/photo_partner/login"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold text-primary-foreground bg-primary hover:bg-primary/90 px-5 py-2 rounded-full transition-colors font-sans"
            >
              Acessar
            </a>
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-foreground p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t border-border pt-4">
            <div className="flex flex-col gap-4">
              {navItems.map(item => (
                <SmoothScrollLink
                  key={item.label}
                  href={item.href}
                  className={cn(
                    'text-sm font-medium tracking-wide font-sans transition-all duration-300',
                    isActive(item)
                      ? 'text-primary font-semibold'
                      : 'text-muted-foreground hover:text-primary',
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </SmoothScrollLink>
              ))}
              <SmoothScrollLink
                href="/parcerias/cadastro"
                className="text-sm font-semibold text-primary border-2 border-primary hover:bg-primary hover:text-primary-foreground px-5 py-2 rounded-full transition-colors text-center font-sans"
                onClick={() => setMobileMenuOpen(false)}
              >
                Cadastre-se
              </SmoothScrollLink>
              <a
                href="https://fotoinfluencer.com.br/photo_partner/login"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-primary-foreground bg-primary hover:bg-primary/90 px-5 py-2 rounded-full transition-colors text-center font-sans"
              >
                Acessar
              </a>
            </div>
          </nav>
        )}
      </header>

      {/* Welcome Banner - Fixed with header */}
      <div className="w-full py-2.5 px-6 md:px-12 lg:px-20 bg-[hsl(5,25%,95%)] border-b border-secondary/30 shadow-sm">
        <p className="text-center font-sans text-sm md:text-base text-foreground">
          Bem-vindo ao Programa de Parcerias da{' '}
          <a
            href="/"
            rel="noopener noreferrer"
            className="text-primary font-semibold hover:underline"
          >
            FotoLivro Store
          </a>
        </p>
      </div>
    </div>
  )
}

export default Header
